import { FC, useEffect, useMemo } from 'react';

import { listGiftItemBrand } from '@shared/api/giftItemBrand/giftItemBrand.controller';
import {
  GiftItemBrand,
  SettlementInfo,
} from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { useAsyncFn } from 'react-use';

// 정산정보: 관리자가 설정한 브랜드별 정산 단가를 조회만 한다 (수정은 관리자 상품관리에서만).
// - 배송관리자(gift-brand-seller): 백엔드 owningFilter로 본인 브랜드 1건만 내려온다.
// - 관리자(admin/GiftAgent): 전체 브랜드를 카드로 나열.

const won = (v?: number) =>
  typeof v === 'number' ? `${v.toLocaleString()}원` : '미설정';
const ea = (v?: number) => (typeof v === 'number' ? `${v.toLocaleString()}개` : '미설정');

// 정산정보가 하나라도 채워진 브랜드인지
const hasSettlement = (brand: GiftItemBrand) => {
  const si = brand.settlementInfo;
  return (
    si != null &&
    (si.shippingFee != null ||
      si.freeShippingMinQuantity != null ||
      si.courierFee != null ||
      si.courierFreeMinQuantity != null ||
      si.settlementDay != null)
  );
};

const InfoRow: FC<{ label: string; value: string; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <div className="flex items-start justify-between gap-4 py-2">
    <div>
      <p className="text-sm text-gray-600">{label}</p>
      {hint && <p className="mt-0.5 text-xs text-gray-400">{hint}</p>}
    </div>
    <p className="whitespace-nowrap text-sm font-medium text-gray-900">{value}</p>
  </div>
);

const SettlementCard: FC<{ brand: GiftItemBrand }> = ({ brand }) => {
  const si: SettlementInfo = brand.settlementInfo ?? {};
  const empty = !hasSettlement(brand);

  return (
    <div className="rounded-lg bg-white p-5 shadow">
      <h3 className="mb-3 text-base font-semibold">{brand.name}</h3>
      {empty ? (
        <p className="rounded-md bg-gray-50 p-3 text-sm text-gray-500">
          아직 등록된 정산정보가 없습니다. 관리자에게 문의해주세요.
        </p>
      ) : (
        <div className="divide-y divide-gray-100">
          <InfoRow
            label="소량주문 추가단가 (개당)"
            value={won(si.shippingFee)}
            hint={`주문 수량이 ${ea(
              si.freeShippingMinQuantity,
            )} 미만이면 개당 가산`}
          />
          <InfoRow
            label="추가단가 면제 최소수량"
            value={ea(si.freeShippingMinQuantity)}
          />
          <InfoRow
            label="택배비 (건당)"
            value={won(si.courierFee)}
            hint={`주문 수량이 ${ea(
              si.courierFreeMinQuantity,
            )} 미만이면 건당 청구`}
          />
          <InfoRow
            label="택배비 무료 최소수량"
            value={ea(si.courierFreeMinQuantity)}
          />
          <InfoRow
            label="정산일 (익월 기준)"
            value={
              typeof si.settlementDay === 'number'
                ? `익월 ${si.settlementDay}일`
                : '미설정'
            }
          />
        </div>
      )}
    </div>
  );
};

export const GiftBrandSettlementInfoPage: FC =
  function GiftBrandSettlementInfoPage() {
    const { giftBrandRole } = useAuth();
    const role = giftBrandRole() as LowerRole.Admin | LowerRole.GiftBrandSeller;
    const isAdmin = role === LowerRole.Admin;

    const [{ loading, value: brands }, fetchBrands] = useAsyncFn(
      async () => listGiftItemBrand(role),
      [role],
    );

    useEffect(() => {
      fetchBrands();
    }, [fetchBrands]);

    // 정산정보가 있는 브랜드를 위로 정렬
    const rows = useMemo(
      () =>
        [...(brands ?? [])].sort(
          (a, b) => Number(hasSettlement(b)) - Number(hasSettlement(a)),
        ),
      [brands],
    );

    return (
      <div>
        <div className="mb-4">
          <h2 className="text-lg font-bold">정산정보</h2>
          <p className="mt-1 text-sm text-gray-500">
            관리자가 설정한 브랜드 정산 단가를 확인하세요.
          </p>
        </div>

        <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-800">
          정산정보는 조회 전용입니다. 변경이 필요하면 관리자에게 문의해주세요.
          {isAdmin && (
            <span className="mt-1 block text-indigo-700">
              관리자는 상품관리 &gt; 브랜드 수정 화면에서 정산정보를 변경할 수
              있습니다.
            </span>
          )}
        </div>

        {loading ? (
          <div className="center-box py-16">
            <Loading />
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
            조회할 정산정보가 없습니다.
          </div>
        ) : (
          <div className="space-y-4">
            {rows.map((brand) => (
              <SettlementCard key={brand._id} brand={brand} />
            ))}
          </div>
        )}
      </div>
    );
  };
